import React, {FC, useEffect, useState} from 'react';
import { IDownloadedData } from './admin-dictionary';
import { Col, Dropdown, Menu, notification, Row, Space, Table } from 'antd';
import {
    deleteDictionary,
    downloadAdminDictionary,
    getDownloadableDictionary,
} from '/react4xp/parts/admin-dictionary/actions';
import Button from '/react4xp/mixins/ui/button/button';
import ThreeDot from 'icons/Threedot.svg';
import CreateDictionaryModal from './components/create-dictionary-modal';
import dayjs from 'dayjs';
import { LOCALIZED_DATA } from './admin-dictionary.constants';
import { capitalizeFirstLetter } from '/react4xp/mixins/helpers';
import {AdminDictionary} from "/react4xp/parts/admin-dictionary/admin-dictionary.type";
import {validatePrivileges} from "mixins/helpers";

const DownloadableDictionary: FC<AdminDictionary.Props> = (props: AdminDictionary.Props) => {
    const [data, setData] = useState<IDownloadedData[]>([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        onLoadedData();
    }, []);

    const onLoadedData = () => {
        setIsLoading(true);
        getDownloadableDictionary({
            latest: false,
        })
            .then(({ data }) => {
                setData(data);
            })
            .finally(() => setIsLoading(false));
    };

    const removeDictionary = (id) => {
        deleteDictionary(id).then(() => {
            notification.success({
                message: 'Справочник удален',
                duration: 3,
            });
            return onLoadedData();
        });
    };

    const handleDownloadDictionary = (metaDocumentId: string) => {
        downloadAdminDictionary(metaDocumentId, props.token).then(({ data }) => {
            const filename = `dictionary_metaDocumentId.xlsx`;
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
        });
    };

    const columns = [
        {
            title: 'Справочник',
            dataIndex: 'name',
        },
        {
            title: 'Описание',
            dataIndex: 'description',
        },
        {
            title: 'Версия',
            dataIndex: 'version',
        },
        {
            title: 'Дата и время обновления',
            dataIndex: 'createdDate',
            render: (item) => dayjs(item).format('DD.MM.YYYY HH:mm:ss'),
        },
        {
            title: 'Статус',
            dataIndex: 'active',
            render: (_, row) => <span key={row.id}>{row.active ? 'Активен' : 'Неактивен'}</span>,
        },
        {
            title: 'Действия',
            width: 100,
            render: (_text, record) => (
                <Space size="middle">
                    <Dropdown
                        overlay={
                            <Menu>
                                {LOCALIZED_DATA.map(({ lang }) => {
                                    const metaDocumentId =
                                        record[`metaDocument${capitalizeFirstLetter(lang)}Id`];
                                    return metaDocumentId ? (
                                        <Menu.Item
                                            key={lang}
                                            onClick={() => handleDownloadDictionary(metaDocumentId)}
                                        >
                                            {`Скачать ${capitalizeFirstLetter(lang)}`}
                                        </Menu.Item>
                                    ) : null;
                                })}
                                {validatePrivileges(props.roles, props.privileges, 'PRIV_DICTIONARY_DELETE') && (
                                    <Menu.Item onClick={() => removeDictionary(record?.id)}>
                                        Удалить
                                    </Menu.Item>
                                )}
                            </Menu>
                        }
                    >
                        <ThreeDot />
                    </Dropdown>
                </Space>
            ),
        },
    ];

    return (
        <div className="poll-screen">
            <Row
                gutter={{ xs: 24, md: 24 }}
                className="my-3"
                justify="space-between"
            >
                <Col></Col>
                <Col>
                    {validatePrivileges(props.roles, props.privileges, 'PRIV_DICTIONARY_UPLOAD') && (
                        <Button
                            color="gold"
                            onClick={() => setIsModalOpen(true)}
                        >
                            Добавить
                        </Button>
                    )}
                </Col>
            </Row>

            <Table
                className="my-3"
                dataSource={data}
                columns={columns}
                loading={isLoading}
            />

            {isModalOpen && (
                <CreateDictionaryModal
                    setDownloadedDictionaries={setData}
                    isOpen={isModalOpen}
                    setIsOpen={setIsModalOpen}
                />
            )}
        </div>
    );
};

export default DownloadableDictionary;
